import { useEffect, useRef, useState } from "react";
import { useWorks, MediaItem, WorkItem } from "../contexts/WorkContext";
import { useAuth } from "../contexts/AuthContext";
import NavBar from "../components/Navbar";
import COLORS from "../constants/colors";
import "./CreateWork.css";
import { BASEURL } from "../constants/baseUrl";

const CreateOrModifyWorkScreen: React.FC = () => {
  const {
    works,
    selectedWork,
    fetchWorks,
    fetchWorkById,
    createWork,
    updateWork,
    deleteWork,
    selectWork,
    loading,
  } = useWorks();

  const { token } = useAuth();
  const descriptionRef = useRef<HTMLDivElement | null>(null);

  // ---------- Mode ----------
  const [mode, setMode] = useState<"create" | "edit">("create");
  const [selectedWorkId, setSelectedWorkId] = useState<number | "">("");

  // ---------- Form States ----------
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<WorkItem["category"]>("film");
  const [videoUrl, setVideoUrl] = useState("");

  const [mainMediaFile, setMainMediaFile] = useState<File | null>(null);
  const [mainMediaPreview, setMainMediaPreview] = useState<string | null>(null);
  const [mainMediaType, setMainMediaType] = useState<"image" | "video">("image");

  const [additionalMedia, setAdditionalMedia] = useState<
    { file?: File; preview: string; type: "image" | "video" }[]
  >([]);

  // ---------- Fetch works ----------
  useEffect(() => {
    fetchWorks();
  }, []);

  // ---------- Auto-fill form when selecting work ----------
  useEffect(() => {
    if (!selectedWork || mode !== "edit") return;

    setTitle(selectedWork.title);
    setDescription(selectedWork.description || "");
    setCategory(selectedWork.category);
    setVideoUrl(selectedWork.video_url || "");

    setMainMediaPreview(selectedWork.image_url || null);
    setMainMediaFile(null);
    setMainMediaType("image");

    setAdditionalMedia(
      selectedWork.media_files?.map((m) => ({
        preview: m.url || "",
        type: m.type,
        file: undefined,
      })) || []
    );

    if (descriptionRef.current) {
      descriptionRef.current.innerHTML = selectedWork.description || "";
    }
  }, [selectedWork, mode]);

  // ---------- Reset Form ----------
  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("film");
    setVideoUrl("");
    setMainMediaFile(null);
    setMainMediaPreview(null);
    setAdditionalMedia([]);
    setSelectedWorkId("");
    selectWork(null);

    if (descriptionRef.current) {
      descriptionRef.current.innerHTML = "";
    }
  };

  // ---------- Handlers ----------
  const handleMainMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    const type = file.type.startsWith("video") ? "video" : "image";

    setMainMediaFile(file);
    setMainMediaType(type);
    setMainMediaPreview(URL.createObjectURL(file));
  };

  const handleAddAdditionalMedia = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith("video") ? "video" : "image" as "image" | "video",
    }));
    setAdditionalMedia((prev) => [...prev, ...files]);
  };

  const handleRemoveAdditionalMedia = (preview: string) => {
    setAdditionalMedia((prev) => prev.filter((m) => m.preview !== preview));
  };

  const handleSubmit = async () => {
    if (!title) return alert("Le titre est obligatoire");

    const media: MediaItem[] = additionalMedia
      .filter((m) => m.file) // uniquement les fichiers uploadés
      .map((m, index) => ({
        type: m.type,
        file: m.file,
        position: index,
      }));

    const payload: WorkItem = {
      title,
      description,
      category,
      image_file: mainMediaFile || undefined,
      image_url: mainMediaPreview || "",
      media_files: media,
      video_url: videoUrl, // URL externe séparée
    };

    if (mode === "create") {
      if (!mainMediaFile) return alert("Affiche principale requise");
      await createWork(payload, token!);
    } else if (mode === "edit" && selectedWorkId) {
      await updateWork(Number(selectedWorkId), payload, token!);
    }

    resetForm();
    setMode("create");
  };

  const handleDelete = async () => {
    if (!selectedWorkId) return;
    if (!window.confirm("Supprimer ce projet ?")) return;

    await deleteWork(Number(selectedWorkId), token!);
    resetForm();
    setMode("create");
  };

  // ---------- Helpers ----------
  const getMediaSrc = (preview: string) => {
    if (preview.startsWith("blob:")) return preview; // fichier local upload
    return `${BASEURL}${preview}`; // fichier serveur
  };

  // ---------- Render ----------
  return (
    <div
      className="creatework-page"
      style={{
        "--primary": COLORS.secondary,
        "--secondary": COLORS.primary,
        "--secondary-hover": COLORS.primary_hover,
      } as React.CSSProperties}
    >
      <NavBar />

      <div className="creatework-container">
        <h1>{mode === "create" ? "CRÉATION D'UN PROJET" : "MODIFICATION D'UN PROJET"}</h1>

        <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
          <button
            type="button"
            onClick={() => { setMode("create"); resetForm(); }}
            style={{ flex: 1 }}
          >
            CRÉER
          </button>
          <button
            type="button"
            onClick={() => setMode("edit")}
            style={{ flex: 1 }}
          >
            MODIFIER
          </button>
        </div>

        {/* Select work */}
        {mode === "edit" && (
          <select
            className="creatework-select"
            value={selectedWorkId}
            onChange={(e) => {
              const id = Number(e.target.value);
              setSelectedWorkId(id);
              fetchWorkById(id);
            }}
          >
            <option value="">Sélectionner un projet</option>
            {works
              .filter((w) => w.id)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.title}
                </option>
              ))}
          </select>
        )}

        {/* --------- Formulaire --------- */}
        <input
          className="creatework-input"
          placeholder="Titre"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className="creatework-label">
          AFFICHE PRINCIPALE
          <input type="file" accept="image/*,video/*" onChange={handleMainMediaChange} />
        </label>

        {mainMediaPreview && (
          <div className="creatework-preview">
            {mainMediaType === "image" ? (
              <img src={getMediaSrc(mainMediaPreview)} alt="affiche preview" />
            ) : (
              <video src={getMediaSrc(mainMediaPreview)} controls />
            )}
          </div>
        )}

        <input
          className="creatework-input"
          placeholder="Vidéo (URL externe)"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
        />

        <label className="creatework-label">
          MÉDIAS SUPPLÉMENTAIRES
          <input type="file" multiple accept="image/*,video/*" onChange={handleAddAdditionalMedia} />
        </label>

        {additionalMedia.length > 0 && (
          <div className="creatework-gallery">
            {additionalMedia.map((m) => (
              <div key={m.preview} className="creatework-thumb">
                {m.type === "image" ? (
                  <img src={getMediaSrc(m.preview)} alt="preview" className="thumbMedia" />
                ) : (
                  <video src={getMediaSrc(m.preview)} controls className="creatework-thumb" preload="metadata" />
                )}
                <button type="button" className="remove-btn" onClick={() => handleRemoveAdditionalMedia(m.preview)}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <select
          className="creatework-select"
          value={category}
          onChange={(e) => setCategory(e.target.value as WorkItem["category"])}
        >
          <option value="film">Film</option>
          <option value="dessin">Dessin</option>
          <option value="illustration">Illustration</option>
          <option value="animation">Animation</option>
        </select>

        <div
          ref={descriptionRef}
          className="creatework-textarea"
          contentEditable
          onInput={(e) => {
            const el = e.currentTarget;
            setDescription(el.innerHTML);
            el.style.height = "auto";
            el.style.height = el.scrollHeight + "px";
          }}
          suppressContentEditableWarning
          style={{ minHeight: "80px", overflow: "hidden", textAlign: "justify", fontFamily: "inherit" }}
        ></div>

        <button className="creatework-submit" onClick={handleSubmit} disabled={loading}>
          {mode === "create" ? (loading ? "CRÉATION..." : "CRÉER !") : (loading ? "MODIFICATION..." : "MODIFIER")}
        </button>

        {mode === "edit" && selectedWorkId && (
          <button
            className="creatework-submit"
            style={{ background: "red", marginTop: "10px" }}
            onClick={handleDelete}
          >
            SUPPRIMER
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateOrModifyWorkScreen;
